import * as yup from 'yup';
import cx from 'classnames';
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { Form } from 'react-bootstrap';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import Button from '../../../../Button/Button';
import {
  MAX_PREVIEW_NAME_LENGTH,
  MIN_PREVIEW_NAME_LENGTH,
} from '../../../../../config/constant/validationConstant';
import { useSaveRenderPreview } from './useSaveRenderPreview';
import styles from './PreviewRenderForm.module.scss';
import { RP_TYPE } from '../../../../../config/constant/unityConstants';
import { errorToast } from '../../../../../helpers/toastHelper';
import { useElementFocus } from '../../../../../hooks/useElementFocus';
import { useUserContext } from '../../../../../contexts/UserContext';
import { usePermissionsHandler } from '../../../../../hooks/usePermissions';
import { PERMISSION } from '../../../../../constants/permissionConstants';
import { NAME_SPECIAL_CHARS_V2 } from '../../../../../constants/validationConstants';
import { isNameValid } from '../../../../../helpers/validationHelper';
import { isUserOnboarding } from '../../../../gamificationTooltip/helpers';
import { isUserComingFromWebsite } from '../../../../../helpers/jsHelper';

export const PreviewRenderForm = ({
  type,
  onSubmit,
  className,
  loading,
  isSiloRoom,
  hasMetadata,
  isAutomation,
  disableImageType = false,
  hideFormButton = false,
  buttonTitle,
  allowWatermark = true,
  resetForm = false,
  setSelectedImageType,
  addDataToSegment = () => {},
}) => {
  const imageTypesDefault = ['jpg', 'png', 'tiff'];
  const imageTypesWithTransparency = ['png', 'tiff'];
  const isOnboarding = isUserOnboarding();
  const { currentUser } = useUserContext();

  const schema = useMemo(() => {
    return yup.object({
      // name: yup
      //   .string()
      //   .min(MIN_PREVIEW_NAME_LENGTH, 'Please provide name')
      //   .max(
      //     MAX_PREVIEW_NAME_LENGTH,
      //     `Name length should not be longer than ${MAX_PREVIEW_NAME_LENGTH}`
      //   )
      //   .required('Name is missing'),
    });
  }, []);

  const defaultImageName = isUserComingFromWebsite()
    ? 'My Preview'
    : isOnboarding
    ? `First image by ${currentUser?.user?.first_name}`
    : '';

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    control,
    watch,
    reset,
    setValue,
    getValues,
    setFocus,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: defaultImageName,
      metadata: false,
      watermark: false,
      transparentBg: false,
      shadowOnFloor: isAutomation ? false : true,
      imageType: 'jpg',
    },
  });

  const isTransparent = watch('transparentBg');
  const isRender = type === RP_TYPE.RENDER;
  const {
    currentUser: {
      organization_detail: { watermark_on_rendering },
    },
  } = useUserContext();
  const { handlePermissionEvent } = usePermissionsHandler();

  useLayoutEffect(() => {
    setFocus('name');
  }, []);

  useEffect(() => {
    setValue('imageType', isTransparent ? 'png' : 'jpg');
  }, [isTransparent]);

  useEffect(() => {
    reset();
  }, [resetForm]);

  const imageTypes = isTransparent ? imageTypesWithTransparency : imageTypesDefault;

  const watermarkRegister = register('watermark');
  const handleWatermarkChange = async (e) => {
    e.preventDefault();    
    const checked = e?.target?.checked;

    if (checked) {
      const result = await handlePermissionEvent(PERMISSION.ADD_WATERMARK, async (e) => {
        watermarkRegister.onChange(e);

        return true;
      })(e);

      setValue('watermark', Boolean(result));
    } else {
      watermarkRegister.onChange(e);
      setTimeout(() => {
        setValue('watermark', false);
      }, 0);
    }
  };

  return (
    <form
      id="produceImageForm"
      onSubmit={handleSubmit(onSubmit)}
      className={cx(styles[className], styles.form)}
      autoComplete="off">
      <input autoComplete="false" name="hidden" type="text" style={{ display: 'none' }} />
      <div className="drop-input">
        <label className="form-label">File name</label>
        <div className="input-group d-flex flex-wrap preview">
          <Controller
            name="name"
            control={control}
            render={({ field: { onChange, onBlur, value, name, ref } }) => {
              const handleNameChange = (e) => {
                e.preventDefault();
                const { value } = e.target;

                if (!isNameValid(value, true)) return;
                addDataToSegment([{ key: 'FileName', value: e.target.value }]);
                onChange(e);
              };

              return (
                <input
                  ref={ref}
                  type="text"
                  name={name}
                  onBlur={onBlur}
                  value={value}
                  disabled={loading}
                  className={`form-control ${styles.nameInput} ${
                    isRender ? styles.renderNameInput : ''
                  }`}
                  onChange={handleNameChange}
                  placeholder={isAutomation ? 'Automation Name' : 'File Name'}
                />
              );
            }}
          />

          {/* {isRender && ( */}
          <div className="input-group-append">
            <select
              className="rp-type"
              {...register('imageType')}
              name="imageType"
              onChange={(e) => setSelectedImageType(e.target.value)}
              disabled={disableImageType}>
              {imageTypes?.map((a) => (
                <option key={a} value={a}>
                  {a.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
          {/* )} */}
        </div>

        <div
          className="invalid-feedback"
          style={{ display: errors.name?.message ? 'block' : 'none' }}>
          {errors.name?.message}
        </div>
      </div>

      {!isUserOnboarding() && !isAutomation && (
        <div
          className={cx(`d-flex flex-wrap`, styles.checkboxContainer, isRender ? 'pt-3' : 'pt-4')}>
          {isRender && hasMetadata && (
            <div className="form-check custom-checkbox d-flex align-items-center ">
              <input
                className="form-check-input"
                type="checkbox"
                name="metadata"
                {...register('metadata')}
              />
              <span>Metadata</span>
            </div>
          )}

          <div className="form-check custom-checkbox d-flex align-items-center ">
            <input
              className="form-check-input"
              type="checkbox"
              name="transparentBg"
              {...register('transparentBg')}
            />
            <span>Transparent Bg</span>
          </div>

          {isRender && allowWatermark && watermark_on_rendering && (
            <div className="form-check custom-checkbox d-flex align-items-center">
              <input
                className="form-check-input"
                type="checkbox"
                name="watermark"
                {...watermarkRegister}
                onChange={handleWatermarkChange}
              />
              <span>Watermark</span>
            </div>
          )}
          {isSiloRoom && (
            <div className="form-check custom-checkbox d-flex align-items-center">
              <input
                className="form-check-input"
                type="checkbox"
                name="shadowOnFloor"
                {...register('shadowOnFloor')}
              />
              <span>Shadow on floor</span>
            </div>
          )}
        </div>
      )}
      <Button
        id="produceImageFormButton"
        type="submit"
        color="warning"
        className={cx(isRender ? 'mt-3' : 'mt-4', 'w-100')}
        size="xlg"
        loading={loading}
        style={hideFormButton ? { display: 'none' } : {}}>
        {isAutomation ? 'Create Automation' : buttonTitle}
        {/* isRender ? 'Send for Rendering' : 'Send for Preview'} */}
      </Button>
    </form>
  );
};
